import { cn } from "@/lib/utils";

/**
 * Blurred food photo used as hero / accent background only (BRD §3.4
 * cost guard + design intent: photos used sparingly).
 *
 * The photo is rendered into an absolutely-positioned layer; the parent
 * must be `position: relative` and clip overflow. Place a cream wash
 * (linear-gradient on top) for legibility.
 */
export function HeroPhoto({
  src,
  blur = 14,
  opacity = 1,
  scale = 1.04,
  className,
}: {
  src: string;
  /** Blur radius in pixels. Set to 0 for a sharp accent. */
  blur?: number;
  opacity?: number;
  scale?: number;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
    >
      <div
        className="absolute -inset-5 bg-cover bg-center"
        style={{
          backgroundImage: `url(${src})`,
          filter: blur ? `blur(${blur}px) saturate(1.05)` : undefined,
          transform: `scale(${scale})`,
          opacity,
        }}
      />
    </div>
  );
}

/** Cream wash overlay for legibility on top of `HeroPhoto`. */
export function HeroWash({
  intensity = "default",
}: {
  intensity?: "default" | "light" | "heavy";
}) {
  const stops = {
    light:
      "linear-gradient(180deg, oklch(var(--background) / 0.35) 0%, oklch(var(--background) / 0.12) 50%, oklch(var(--background) / 0.55) 100%)",
    default:
      "linear-gradient(180deg, oklch(var(--background) / 0.55) 0%, oklch(var(--background) / 0.22) 35%, oklch(var(--background) / 0.5) 70%, oklch(var(--background)) 100%)",
    heavy:
      "linear-gradient(180deg, oklch(var(--background) / 0.85) 0%, oklch(var(--background) / 0.55) 50%, oklch(var(--background)) 100%)",
  }[intensity];
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{ background: stops }}
    />
  );
}
